/*!

=========================================================
* CradleAI - v1.2.4
=========================================================
*/

import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
} from "reactstrap";
// core components
import UserHeader from "components/Headers/UserHeader.js";
import { useEffect, useRef, useState } from "react";
import { getAdminProfile, updateAdminProfile, } from "api/ApiServices";
import { Image_Url } from "api/ApiClient";
import Swal from "sweetalert2";
import avatar from "assets/img/brand/avatar-2.jpg";

const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_PATH || Image_Url;

const Profile = () => {
  const [adminData, setAdminData] = useState({
    firstName: "",
    lastName: "",
    name: "",
    email: "",
    phone: "",
    image: "",
    imageFile: null,
    address: "",
    password: "",
    confirmPassword: "",
    au_2fa_enabled: false,
  });

  const [errors, setErrors] = useState({
    firstName: false,
    email: false,
    phone: false,
    address: false,
    password: false,
    confirmPassword: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await getAdminProfile();
        console.log("Admin Profile Data:", response);
        setAdminData((prev) => ({
          ...prev,
          ...response.payload,
        }));
      } catch (error) {
        console.error("Failed to fetch profile:", error);
      }
    };

    fetchProfile();
  }, []);

  const handleImageClick = (e) => {
    e.preventDefault();
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setAdminData((prev) => ({
        ...prev,
        image: previewUrl,
        imageFile: file,
      }));
      // Dispatch custom event to sync with navbar immediately
      window.dispatchEvent(new CustomEvent("adminProfileImageUpdated", { detail: previewUrl }));
    }
  };

  const handleUpdate = async (e) => {
    console.log("Admin Data:", adminData);
    e.preventDefault();

    let pwdError = false;
    let confirmPwdError = false;

    if (adminData.password) {
      if (adminData.password.length < 6 || adminData.password.length > 20) {
        pwdError = "Password must be between 6 and 20 characters.";
      } else if (!/(?=.*[a-zA-Z])(?=.*\d)/.test(adminData.password)) {
        pwdError = "Password must contain at least one letter and one digit.";
      }

      if (adminData.password !== adminData.confirmPassword) {
        confirmPwdError = "Passwords do not match.";
      }
    } else if (adminData.confirmPassword) {
      pwdError = "Please enter a password.";
    }

    const newErrors = {
      firstName: !adminData.firstName || adminData.firstName.trim() === "",
      email: !adminData.email || adminData.email.trim() === "",
      phone: !adminData.phone || adminData.phone.toString().trim() === "",
      address: !adminData.address || adminData.address.trim() === "",
      password: pwdError,
      confirmPassword: confirmPwdError,
    };

    setErrors(newErrors);

    const hasErrors = Object.values(newErrors).some((error) => error);

    if (hasErrors) {
      return;
    }

    const formData = new FormData();
    formData.append("name", adminData.firstName);
    formData.append("surname", adminData.lastName);
    formData.append("email", adminData.email);
    formData.append("phone", adminData.phone);
    formData.append("address", adminData.address);
    formData.append("au_2fa_enabled", adminData.au_2fa_enabled);

    if (adminData.password) {
      formData.append("password", adminData.password);
    }

    if (adminData.imageFile) {
      formData.append("image", adminData.imageFile);
    }

    try {
      const updated = await updateAdminProfile(formData);
      if (updated && updated.status !== false) {
        const isPasswordChanged = !!adminData.password;
        Swal.fire({
          title: isPasswordChanged ? "Password Changed!" : "Profile Updated!",
          text: isPasswordChanged
            ? "Your password has been changed successfully. Please log in again with your new password."
            : (updated.message || "Your profile has been successfully updated."),
          icon: "success",
          timer: isPasswordChanged ? 3000 : 1500,
          showConfirmButton: isPasswordChanged,
        }).then(() => {
          if (isPasswordChanged) {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            localStorage.removeItem("adminPermissions");
            localStorage.removeItem("userRole");
            localStorage.removeItem("userPermissions");
            window.location.href = "/auth/login";
          } else {
            window.location.reload();
          }
        });
      } else {
        Swal.fire({
          title: "Error!",
          text: updated?.message || "Failed to update profile.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (err) {
      console.error("Update failed:", err);
      const errorMsg = err.response?.data?.message || "Failed to update profile.";
      Swal.fire({
        title: "Error!",
        text: errorMsg,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <>
      <UserHeader adminData={adminData} />
      <Container className="mt--7" fluid>
        <Row>
          <Col className="order-xl-2 mb-5 mb-xl-0" xl="4">
            <Card className="card-profile shadow">
              <Row className="justify-content-center">
                <Col className="order-lg-2" lg="3">
                  <div className="card-profile-image" style={{ textAlign: "center" }}>
                    <a href="#pablo" onClick={handleImageClick}>
                      <img
                        alt="Admin Profile"
                        className="rounded-circle"
                        src={
                          adminData.image
                            ? adminData.image.startsWith("blob:")
                              ? adminData.image
                              : `${IMAGE_BASE_URL}${adminData.image}`
                            : avatar
                        }
                        style={{
                          width: "120px",
                          height: "120px",
                          objectFit: "cover",
                          border: "4px solid #fff",
                          display: "block",
                          margin: "0 auto",
                        }}
                      />
                    </a>
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      onChange={handleFileChange}
                    />
                  </div>
                </Col>
              </Row>
              <CardHeader className="text-center border-0 pt-8 pt-md-4 pb-0 pb-md-4"></CardHeader>
              <CardBody className="pt-0 pt-md-4">
                <div className="text-center mt-7">
                  <h3>
                    {adminData.firstName} {adminData.lastName}
                  </h3>
                  <div className="h5 font-weight-300">
                    <i className="ni location_pin mr-2" />
                    {adminData.address}
                  </div>
                  <div className="h5 mt-4">
                    <i className="ni business_briefcase-24 mr-2" />
                    {adminData.email}
                  </div>
                  <div>
                    <i className="ni ni-favourite-28 mr-2" />
                    Manage CradleAI
                  </div>
                  <hr className="my-4" />
                  <p>
                    CradleAI Chat
                  </p>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col className="order-xl-1" xl="8">
            <Card className="bg-secondary shadow">
              <CardHeader className="bg-white border-0">
                <Row className="align-items-center">
                  <Col xs="8">
                    <h3 className="mb-0">My account</h3>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <Form>
                  <h6 className="heading-small text-muted mb-4">
                    User information
                  </h6>
                  <div className="pl-lg-4">
                    {/* --- User Info --- */}
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-first-name"
                          >
                            First name <span style={{ color: "red" }}>*</span>
                          </label>
                          <Input
                            className={`form-control-alternative ${errors.firstName ? "border-danger" : ""}`}
                            value={adminData.firstName || ""}
                            id="input-first-name"
                            placeholder="First name"
                            type="text"
                            onChange={(e) =>
                              setAdminData({
                                ...adminData,
                                firstName: e.target.value,
                              })
                            }
                          />
                          {errors.firstName && (
                            <small className="text-danger">
                              First Name is required.
                            </small>
                          )}
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-last-name"
                          >
                            Last name
                          </label>
                          <Input
                            className="form-control-alternative"
                            value={adminData.lastName || ""}
                            id="input-last-name"
                            placeholder="Last name"
                            type="text"
                            onChange={(e) =>
                              setAdminData({
                                ...adminData,
                                lastName: e.target.value,
                              })
                            }
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-phone"
                          >
                            Phone <span style={{ color: "red" }}>*</span>
                          </label>
                          <Input
                            className={`form-control-alternative ${errors.phone ? "border-danger" : ""}`}
                            value={adminData.phone || ""}
                            id="input-phone"
                            placeholder="Phone number"
                            maxLength={10}
                            type="text"
                            onChange={(e) => {
                              const onlyNums = e.target.value.replace(
                                /[^0-9]/g,
                                ""
                              );
                              setAdminData({
                                ...adminData,
                                phone: onlyNums,
                              });
                            }}
                          />
                          {errors.phone && (
                            <small className="text-danger">
                              Phone Number is required.
                            </small>
                          )}
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-email"
                          >
                            Email address{" "}
                            <span style={{ color: "red" }}>*</span>
                          </label>

                          <Input
                            className={`form-control-alternative ${errors.email ? "border-danger" : ""
                              }`}
                            id="input-email"
                            placeholder="jesse@example.com"
                            type="email"
                            value={adminData.email || ""}
                            onChange={(e) =>
                              setAdminData({
                                ...adminData,
                                email: e.target.value,
                              })
                            }
                            readOnly={false} // Make sure it's not read-only
                          />

                          {errors.email && (
                            <small className="text-danger">
                              Email is required.
                            </small>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                  <hr className="my-4" />
                  {/* --- Contact Info --- */}
                  <h6 className="heading-small text-muted mb-4">
                    Contact information
                  </h6>
                  <div className="pl-lg-4">
                    <Row>
                      <Col md="12">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-address"
                          >
                            Address <span style={{ color: "red" }}>*</span>
                          </label>
                          <Input
                            className={`form-control-alternative ${errors.address ? "border-danger" : ""}`}
                            value={adminData.address || ""}
                            id="input-address"
                            placeholder="Home Address"
                            type="text"
                            onChange={(e) =>
                              setAdminData({
                                ...adminData,
                                address: e.target.value,
                              })
                            }
                          />
                          {errors.address && (
                            <small className="text-danger">
                              Address is required.
                            </small>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>

                  <hr className="my-4" />
                  {/* --- Change Password --- */}
                  <h6 className="heading-small text-muted mb-4">
                    Change Password
                  </h6>
                  <div className="pl-lg-4">
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">
                            New Password <span className="text-danger">*</span>
                          </label>
                          <InputGroup className="input-group-alternative">
                            <Input
                              type={showPassword ? "text" : "password"}
                              placeholder="Enter new password"
                              value={adminData.password}
                              onChange={(e) =>
                                setAdminData({
                                  ...adminData,
                                  password: e.target.value,
                                })
                              }
                              className={`form-control-alternative ${errors.password ? "border-danger" : ""
                                }`}
                            />
                            <InputGroupAddon addonType="append">
                              <InputGroupText
                                onClick={() =>
                                  setShowPassword(!showPassword)
                                }
                                style={{ cursor: "pointer" }}
                              >
                                <i
                                  className={
                                    showPassword
                                      ? "fas fa-eye-slash"
                                      : "fas fa-eye"
                                  }
                                />
                              </InputGroupText>
                            </InputGroupAddon>
                          </InputGroup>
                          {errors.password && (
                            <small className="text-danger d-block">
                              {typeof errors.password === "string" ? errors.password : "Password must be at least 6 characters."}
                            </small>
                          )}
                          <small className="text-muted d-block mt-1">
                            Password must be 6-20 characters long and include at least one letter and one number.
                          </small>
                        </FormGroup>
                      </Col>

                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">
                            Confirm Password <span className="text-danger">*</span>
                          </label>
                          <InputGroup className="input-group-alternative">
                            <Input
                              type={showConfirmPassword ? "text" : "password"}
                              placeholder="Confirm new password"
                              value={adminData.confirmPassword}
                              onChange={(e) =>
                                setAdminData({
                                  ...adminData,
                                  confirmPassword: e.target.value,
                                })
                              }
                              className={`form-control-alternative ${errors.confirmPassword ? "border-danger" : ""
                                }`}
                            />
                            <InputGroupAddon addonType="append">
                              <InputGroupText
                                onClick={() =>
                                  setShowConfirmPassword(!showConfirmPassword)
                                }
                                style={{ cursor: "pointer" }}
                              >
                                <i
                                  className={
                                    showConfirmPassword
                                      ? "fas fa-eye-slash"
                                      : "fas fa-eye"
                                  }
                                />
                              </InputGroupText>
                            </InputGroupAddon>
                          </InputGroup>
                          {errors.confirmPassword && (
                            <small className="text-danger">
                              {typeof errors.confirmPassword === "string" ? errors.confirmPassword : "Passwords do not match."}
                            </small>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>

                  <div className="pl-lg-4">
                    <Row>
                      <Col className="text-start " xs="4">
                        <Button
                          color="success"
                          href="#pablo"
                          onClick={handleUpdate}
                          size="sm"
                        >
                          Update
                        </Button>
                      </Col>
                    </Row>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Profile;
